import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './i18n'; // Import i18n configuration

import Header from './components/Header';
import Navigation from './components/Navigation';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import Projects from './components/Projects';
import ProjectDetail from './components/ProjectDetail';
import Attendance from './components/Attendance';
import VacationRequests from './components/VacationRequests';
import StatisticsImproved from './components/StatisticsImproved';
import HoursAdjustment from './components/HoursAdjustment';
import HoursImport from './components/HoursImport';
import Documents from './components/Documents';
import TermsModal from './components/TermsModal';
import AdminTermsPanel from './components/AdminTermsPanel';
import AdminAuditLog from './components/AdminAuditLog';
import AdminDevTasks from './components/AdminDevTasks';
import AdminQueueHealth from './components/AdminQueueHealth';
import UserTermsHistory from './components/UserTermsHistory';
import UserProfile from './components/UserProfile';
import SessionWarningModal from './components/SessionWarningModal';
import Directory from './components/Directory';
import WorkReport from './components/WorkReport';
import WorkReportUsers from './components/WorkReportUsers';

import api from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setShowTermsModal(false);
    setTermsChecked(false);
    setShowSessionWarning(false);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Session timeout: warning at 40 min, auto-logout at 45 min
  useEffect(() => {
    if (!user) return;

    let warningTimer;
    let logoutTimer;
    const WARNING_TIMEOUT = 40 * 60 * 1000; // 40 minutes
    const LOGOUT_TIMEOUT = 45 * 60 * 1000; // 45 minutes

    // Show warning after 40 minutes
    warningTimer = setTimeout(() => {
      setShowSessionWarning(true);
    }, WARNING_TIMEOUT);

    // Auto-logout after 45 minutes
    logoutTimer = setTimeout(() => {
      handleLogout();
    }, LOGOUT_TIMEOUT);

    // Cleanup
    return () => {
      if (warningTimer) clearTimeout(warningTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
    };
  }, [user, handleLogout]);

  // Check terms acceptance status after user is set
  useEffect(() => {
    const checkTermsAcceptance = async () => {
      if (!user || termsChecked) return;
      // Admin never needs to accept terms in UI (can manage them)
      if (user.role === 'admin') { setTermsChecked(true); setShowTermsModal(false); return; }

      try {
        // Použi centrálne API (s automatickým tokenom) namiesto fetch na /api
        const { data } = await api.get('/terms/user/status');
        if (data && data.requiresAcceptance) {
          setShowTermsModal(true);
        }
        setTermsChecked(true);
      } catch (error) {
        console.error('Error checking terms status:', error);
        setTermsChecked(true);
      }
    };

    checkTermsAcceptance();
  }, [user, termsChecked]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
    setTermsChecked(true);
  };

  if (loading) {
    return (
      <div className="loading">
        Načítava sa ConstructAP systém...
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <ToastContainer position="top-right" />
      </>
    );
  }

  return (
    <Router>
      <div className="App">
        <Header user={user} onLogout={handleLogout} />
        <Navigation user={user} />
        
        <main className="container">
          <Routes>
            <Route path="/" element={<Dashboard user={user} />} />
            <Route path="/dashboard" element={<Dashboard user={user} />} />
            <Route path="/users" element={<Users user={user} />} />
            <Route path="/users/:id" element={<UserProfile />} />
            <Route path="/projects" element={<Projects user={user} />} />
            <Route path="/projects/:projectId" element={<ProjectDetail user={user} />} />
            <Route path="/attendance" element={<Attendance user={user} />} />
            <Route path="/vacation-requests" element={<VacationRequests user={user} />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/statistics" element={<StatisticsImproved user={user} />} />
            <Route path="/hours-adjustment" element={<HoursAdjustment user={user} />} />
            <Route path="/hours-import" element={<HoursImport user={user} />} />
            <Route path="/documents" element={<Documents user={user} />} />
            <Route path="/work-report/:year/:month" element={<WorkReport user={user} />} />
            <Route path="/work-report" element={<WorkReportUsers user={user} />} />
            <Route path="/admin/terms" element={<AdminTermsPanel />} />
            <Route path="/admin/audit-log" element={<AdminAuditLog user={user} />} />
            <Route path="/admin/dev-tasks" element={<AdminDevTasks user={user} />} />
            <Route path="/admin/queue-health" element={<AdminQueueHealth user={user} />} />
            <Route path="/profile/terms" element={<UserTermsHistory />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        <TermsModal 
          show={showTermsModal} 
          onAccept={handleTermsAccept}
          onLogout={handleLogout}
        />
        
        <SessionWarningModal
          show={showSessionWarning}
          onClose={() => setShowSessionWarning(false)}
          onLogout={handleLogout}
        />
        
        <ToastContainer position="top-right" />
      </div>
    </Router>
  );
}

export default App;